<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Header with actions -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto">
        <v-btn 
          color="primary"
          @click="openCreateDialog"
          prepend-icon="mdi-plus"
        >
          Добавить
        </v-btn>
        <v-btn
          v-if="showBulkActions && selectedItems.length > 0"
          color="error"
          variant="outlined"
          class="ml-2"
          @click="bulkDelete"
          prepend-icon="mdi-delete"
        >
          Удалить ({{ selectedItems.length }})
        </v-btn>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">{{ title }}</h4>
      </v-col>
      <v-col cols="auto">
        <!-- Pagination controls -->
        <div class="d-flex align-center ga-2">
          <span class="text-caption">На странице:</span>
          <v-select
            v-model="itemsPerPage"
            :items="[10, 25, 50, 100]"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 100px"
            @update:model-value="onPageSizeChange"
          ></v-select>
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            size="small"
            @update:model-value="onPageChange"
          ></v-pagination>
          <span class="text-caption font-weight-bold">Всего: {{ totalItems }}</span>
        </div>
      </v-col>
    </v-row>

    <!-- Filters section (only for entities that need it) -->
    <v-row v-if="showFilters" class="mb-3">
      <v-col>
        <v-expansion-panels v-model="filtersExpanded" multiple>
          <v-expansion-panel value="filters">
            <v-expansion-panel-title>
              <v-icon start>mdi-filter</v-icon>
              Фильтры
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <slot name="filters"></slot>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <!-- Table section -->
    <v-card>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :loading="loading"
          :items-per-page="itemsPerPage"
          class="elevation-1"
          hover
          fixed-header
          height="calc(100vh - 276px)"
          show-select
          v-model="selectedItems"
          item-value="id"
          hide-default-footer
        >
          <!-- Dynamic column slots -->
          <template v-for="header in headers" v-slot:[`item.${header.key}`]="{ item }" :key="header.key">
            <slot :name="`item.${header.key}`" :item="item">
              {{ item[header.key] }}
            </slot>
          </template>

          <!-- Actions column -->
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              size="small"
              variant="text"
              icon="mdi-pencil"
              @click="editItem(item)"
            ></v-btn>
            <v-btn
              size="small"
              variant="text"
              icon="mdi-delete"
              color="error"
              @click="deleteItem(item)"
            ></v-btn>
          </template>

          <!-- No data state -->
          <template v-slot:no-data>
            <div class="text-center py-6">
              <v-icon size="64" color="grey-lighten-2">{{ noDataIcon }}</v-icon>
              <p class="text-h6 text-grey-darken-1 mt-4">{{ noDataText }}</p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog 
      v-model="showDialog" 
      max-width="600px"
      @keydown.enter="handleEnterKey"
      @keydown.esc="closeDialog"
    >
      <v-card>
        <v-card-title class="text-h5 d-flex align-center justify-space-between">
          <span>{{ editMode ? 'Редактировать' : 'Добавить' }} {{ entityName.toLowerCase() }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeDialog"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          <v-form 
            ref="form" 
            v-model="formValid"
            @submit.prevent="handleFormSubmit"
          >
            <slot name="form-fields" :item="currentItem" :editMode="editMode"></slot>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Отмена</v-btn>
          <v-btn 
            color="primary" 
            :disabled="!formValid" 
            :loading="saving"
            @click="saveItem"
          >
            {{ editMode ? 'Сохранить' : 'Создать' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog 
      v-model="showDeleteDialog" 
      max-width="400px"
      @keydown.enter="confirmDelete"
      @keydown.esc="showDeleteDialog = false"
    >
      <v-card>
        <v-card-title class="text-h5 d-flex align-center justify-space-between">
          <span>Подтверждение удаления</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showDeleteDialog = false"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          Вы уверены, что хотите удалить выбранные элементы?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Отмена</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from 'vue'

export default {
  name: 'BaseReferenceTable',
  props: {
    title: {
      type: String,
      required: true
    },
    entityName: {
      type: String,
      required: true
    },
    headers: {
      type: Array,
      required: true
    },
    api: {
      type: Object,
      required: true
    },
    showFilters: {
      type: Boolean,
      default: false
    },
    showBulkActions: {
      type: Boolean,
      default: true
    },
    noDataIcon: {
      type: String,
      default: 'mdi-database'
    },
    noDataText: {
      type: String,
      default: 'Нет данных для отображения'
    }
  },
  emits: [
    'create-item',
    'update-item', 
    'delete-item',
    'bulk-delete',
    'load-data'
  ],
  setup(props, { emit }) {
    // Reactive variables
    const items = ref([])
    const loading = ref(false)
    const saving = ref(false)
    const deleting = ref(false)
    const selectedItems = ref([])
    
    // Pagination
    const currentPage = ref(1)
    const itemsPerPage = ref(25)
    const totalItems = ref(0)
    
    // Dialog states
    const showDialog = ref(false)
    const showDeleteDialog = ref(false)
    const editMode = ref(false)
    const currentItem = ref({})
    const formValid = ref(false)
    
    // Filters
    const filtersExpanded = ref(['filters'])
    
    // Snackbar
    const snackbar = ref({
      show: false,
      message: '',
      color: 'success'
    })

    // Computed
    const totalPages = computed(() => {
      return Math.ceil(totalItems.value / itemsPerPage.value)
    })

    const tableHeaders = computed(() => {
      return [
        ...props.headers,
        { 
          title: 'Действия', 
          key: 'actions', 
          sortable: false, 
          width: 120,
          align: 'center'
        }
      ]
    })

    // Methods
    const loadData = async () => {
      loading.value = true
      try {
        emit('load-data', {
          page: currentPage.value,
          limit: itemsPerPage.value,
          callback: (data, errorMessage) => {
            loading.value = false
            if (data) {
              items.value = data.items || []
              totalItems.value = data.total || 0
            } else {
              showMessage(errorMessage || 'Ошибка загрузки данных', 'error')
              items.value = []
              totalItems.value = 0
            }
          }
        })
      } catch (error) {
        loading.value = false
        showMessage('Ошибка загрузки данных', 'error')
        items.value = []
        totalItems.value = 0
      }
    }

    const onPageChange = (page) => {
      currentPage.value = page
      loadData()
    }

    const onPageSizeChange = () => {
      currentPage.value = 1
      loadData()
    }

    const openCreateDialog = () => {
      editMode.value = false
      currentItem.value = {}
      showDialog.value = true
    }

    const editItem = (item) => {
      editMode.value = true
      currentItem.value = { ...item }
      showDialog.value = true
    }

    const closeDialog = () => {
      showDialog.value = false
      currentItem.value = {}
      formValid.value = false
    }

    const saveItem = () => {
      saving.value = true
      
      if (editMode.value) {
        emit('update-item', {
          item: currentItem.value,
          callback: (success, errorMessage) => {
            saving.value = false
            if (success) {
              closeDialog()
              loadData()
              showMessage('Элемент успешно обновлен')
            } else {
              showMessage(errorMessage || 'Не удалось сохранить изменения. Попробуйте еще раз.', 'error')
            }
          }
        })
      } else {
        emit('create-item', {
          item: currentItem.value,
          callback: (success, errorMessage) => {
            saving.value = false
            if (success) {
              closeDialog()
              loadData()
              showMessage('Элемент успешно создан')
            } else {
              showMessage(errorMessage || 'Не удалось создать элемент. Попробуйте еще раз.', 'error')
            }
          }
        })
      }
    }

    const deleteItem = (item) => {
      selectedItems.value = [item.id]
      showDeleteDialog.value = true
    }

    const bulkDelete = () => {
      if (selectedItems.value.length > 0) {
        showDeleteDialog.value = true
      }
    }

    const confirmDelete = () => {
      deleting.value = true
      
      emit('delete-item', {
        ids: selectedItems.value,
        callback: (success, errorMessage) => {
          deleting.value = false
          if (success) {
            showDeleteDialog.value = false
            selectedItems.value = []
            loadData()
            showMessage('Элементы успешно удалены')
          } else {
            showMessage(errorMessage || 'Не удалось удалить элементы. Попробуйте еще раз.', 'error')
          }
        }
      })
    }

    const showMessage = (message, color = 'success') => {
      snackbar.value = {
        show: true,
        message,
        color
      }
    }

    // Keyboard handlers
    const handleEnterKey = (event) => {
      // Only handle if form is valid and not already saving
      if (formValid.value && !saving.value) {
        event.preventDefault()
        saveItem()
      }
    }

    const handleFormSubmit = () => {
      // Prevent default form submission behavior
      if (formValid.value && !saving.value) {
        saveItem()
      }
    }

    // Lifecycle
    onMounted(() => {
      loadData()
    })

    return {
      // Data
      items,
      loading,
      saving,
      deleting,
      selectedItems,
      currentPage,
      itemsPerPage,
      totalItems,
      totalPages,
      showDialog,
      showDeleteDialog,
      editMode,
      currentItem,
      formValid,
      filtersExpanded,
      snackbar,
      tableHeaders,

      // Methods
      loadData,
      onPageChange,
      onPageSizeChange,
      openCreateDialog,
      editItem,
      closeDialog,
      saveItem,
      deleteItem,
      bulkDelete,
      confirmDelete,
      showMessage,
      handleEnterKey,
      handleFormSubmit
    }
  }
}
</script>

<style scoped>
.v-data-table {
  font-size: 0.875rem;
}

/* Ensure proper text truncation in table cells */
.v-data-table :deep(.v-data-table__td) {
  padding: 8px 16px !important;
  height: 60px !important;
}

.v-data-table :deep(.v-data-table__th) {
  padding: 8px 16px !important;
  font-weight: 600;
  white-space: nowrap;
}

/* Reduce spacing between columns except first two */
.v-data-table :deep(.v-data-table__td:not(:nth-child(1)):not(:nth-child(2))) {
  padding-left: 8px !important;
  padding-right: 8px !important;
}

.v-data-table :deep(.v-data-table__th:not(:nth-child(1)):not(:nth-child(2))) {
  padding-left: 8px !important;
  padding-right: 8px !important;
}

/* Extra spacing after checkbox column */
.v-data-table :deep(.v-data-table__td:nth-child(2)) {
  padding-left: 20px !important;
}

/* Ensure text doesn't wrap in cells */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Compact action buttons */
.v-btn--size-x-small {
  height: 28px !important;
  width: 28px !important;
}

/* Better alignment for numbers */
.text-right {
  text-align: right !important;
}
</style>